import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { Box, Container, Typography, TableBody, TableContainer, useTheme, useMediaQuery, Button, CircularProgress, Alert, IconButton, Paper, TextField, MenuItem, Autocomplete, TableFooter, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import Rating from '@mui/material/Rating';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LoopIcon from '@mui/icons-material/Loop';
import { useNavigate } from 'react-router-dom';
import { BusinessContext } from '../context/BusinessContext';
import Tooltip from '@mui/material/Tooltip';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddBusinessForm from '../components/AddBusinessForm';
import InfoIcon from '@mui/icons-material/Info';
import SubscriptionAlert from '../components/SubscriptionAlert';
import { formatCurrency } from '../utils/formatUtils';
import SearchIcon from '@mui/icons-material/Search';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import currencies from '../constants/currencies.json';

const API_URL = process.env.REACT_APP_API_URL;

// Custom hook to measure container width and determine which columns to display
const useResponsiveColumns = (containerRef) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [columns, setColumns] = useState({
    showAddress: true,
    showRatingStars: true,
    showTotalReviews: true,
    showReviewCount: true
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Setup ResizeObserver
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Determine which columns to show based on actual available width
  useEffect(() => {
    // Calculate which columns to show based on container width
    const newColumnState = {
      showAddress: containerWidth > 370,
      showRatingStars: containerWidth > 350,
      showTotalReviews: containerWidth > 500,
      showReviewCount: containerWidth > 700
    };

    setColumns(newColumnState);
  }, [containerWidth]);

  return columns;
};

// Add at the top of the file with other utility functions
const formatErrorMessage = (error) => {
  if (!error) return "";
  if (typeof error === 'string') return error;
  // Handle FastAPI validation error objects
  if (error.msg) return error.msg;
  if (Array.isArray(error)) return error.map(e => e.msg || String(e)).join(', ');
  // Generic object handling
  return JSON.stringify(error);
};

const Businesses = () => {
  const [businessesData, setBusinessesData] = useState([]);
  const { selectedBusiness, updateSelectedBusiness } = useContext(BusinessContext);
  const tableContainerRef = useRef(null);
  const { showAddress, showRatingStars, showTotalReviews } = useResponsiveColumns(tableContainerRef);
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // Add check for medium screens and below
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    subscription_status: null,
    trial_ends_at: null,
    is_active: false,
    user_role: null,
  });
  
  const [pollingIntervalId, setPollingIntervalId] = useState(null);

  // State for inline add business form
  const [addInput, setAddInput] = useState('');
  const [addSuggestions, setAddSuggestions] = useState([]);
  const [addPlaceId, setAddPlaceId] = useState('');
  const [addBusinessName, setAddBusinessName] = useState('');
  const [addBusinessAddress, setAddBusinessAddress] = useState('');
  const [addTurnover, setAddTurnover] = useState('');
  const [addCurrency, setAddCurrency] = useState('USD');
  const [addSessionToken, setAddSessionToken] = useState('');
  const [addError, setAddError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  
  const currencyList = Object.values(currencies).sort((a, b) => a.name.localeCompare(b.name));

  // Add state for confirmation dialog
  const [stopMonitoringDialogOpen, setStopMonitoringDialogOpen] = useState(false);
  const [businessToStopMonitoring, setBusinessToStopMonitoring] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/users/subscription-status`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch subscription status');
      const data = await response.json();
      setSubscriptionStatus({
        subscription_status: data.subscription_status,
        trial_ends_at: data.trial_ends_at,
        is_active: data.is_active,
        user_role: data.user_role,
      });
      console.log('Subscription status data:', data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setSubscriptionStatus(prev => ({ ...prev, is_active: false }));
    }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/businesses/`, { credentials: 'include' });
       if (!response.ok) throw new Error('Failed to fetch businesses');
      const data = await response.json();
      setBusinessesData(data || []);
      setError(null); // Clear previous errors on successful fetch
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setError('Failed to load businesses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Define handleBusinessSelection before the useEffect that uses it
  const handleBusinessSelection = useCallback(async (business) => {
    // Check the is_monitored flag directly from the business object
    if (!business.is_monitored) {
      console.log("Cannot select a business that is not monitored.");
      // Optionally show a notification to the user
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/users/business/${business.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        updateSelectedBusiness(business);
      } else {
        console.error('Failed to update selected business in the database');
      }
    } catch (error) {
      console.error('Error selecting business:', error);
    }
  }, [updateSelectedBusiness]);

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchSubscriptionStatus();
      await fetchBusinesses();
    };
    
    fetchInitialData();
  }, [fetchSubscriptionStatus, fetchBusinesses]);

  // Auto-select a business if none is selected
  useEffect(() => {
    // Skip if loading or if there are no businesses
    if (loading || businessesData.length === 0) {
      return;
    }

    // Filter to get only monitored businesses that aren't pending
    const monitoredBusinesses = businessesData.filter(business => {
      const isPending = business.status === 'pending';
      return !isPending && business.is_monitored;
    });

    // If there are monitored businesses and none is selected, select the first one
    if (monitoredBusinesses.length > 0 && !selectedBusiness) {
      console.log("Auto-selecting business:", monitoredBusinesses[0].name);
      handleBusinessSelection(monitoredBusinesses[0]);
    }
    // Pass businessesData as dependency since is_monitored is now part of it
  }, [businessesData, loading, selectedBusiness, handleBusinessSelection]);

  // --- Polling Logic ---
  useEffect(() => {
    // Don't poll if nothing is pending
    const hasPendingBusiness = businessesData.some(business => business.status === 'pending');
    if (!hasPendingBusiness) return;
    
    console.log("Starting polling for pending businesses");
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/businesses/`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          console.log("Polling update received");
          setBusinessesData(data || []);
          
          // Check if all pending businesses are now processed
          const stillHasPending = data.some(business => business.status === 'pending');
          if (!stillHasPending) {
            console.log("No more pending businesses, stopping polling");
            clearInterval(intervalId);
          }
        }
      } catch (error) {
        console.error('Error polling businesses:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => {
      console.log("Cleaning up polling interval");
      clearInterval(intervalId);
    };
  }, [businessesData]);

  const handleStopMonitoringClick = (e, business) => {
    e.stopPropagation(); // Prevent row click event
    setBusinessToStopMonitoring(business);
    setStopMonitoringDialogOpen(true);
  };

  const handleConfirmStopMonitoring = async () => {
    if (!businessToStopMonitoring) return;
    
    // Check if we're removing the currently selected business
    const isRemovingSelected = selectedBusiness?.id === businessToStopMonitoring.id;
    
    try {
      const response = await fetch(`${API_URL}/businesses/${businessToStopMonitoring.id}/monitor`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_monitored: false }),
      });
      if (response.ok) {
        // Refresh business list
        await fetchBusinesses();
        
        // If we removed the selected business, select another one if available
        if (isRemovingSelected) {
          // Get updated list of businesses
          const response = await fetch(`${API_URL}/businesses/`, { credentials: 'include' });
          if (response.ok) {
            const updatedBusinesses = await response.json();
            
            // Find businesses that are monitored and not pending
            const monitoredBusinesses = updatedBusinesses.filter(business => {
              const isPending = business.status === 'pending';
              const isMonitored = business.is_monitored;
              return !isPending && isMonitored;
            });
            
            // If there are other businesses, select the first one
            if (monitoredBusinesses.length > 0) {
              console.log("Auto-selecting new business after removal:", monitoredBusinesses[0].name);
              handleBusinessSelection(monitoredBusinesses[0]);
            } else {
              // No more businesses to select, clear selection
              updateSelectedBusiness(null);
            }
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to stop monitoring.');
        console.error('Failed to stop monitoring business');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('Error stopping monitoring:', error);
    } finally {
      setStopMonitoringDialogOpen(false);
      setBusinessToStopMonitoring(null);
    }
  };

  const handleCloseStopMonitoringDialog = () => {
    setStopMonitoringDialogOpen(false);
    setBusinessToStopMonitoring(null);
  };

  // --- Inline Add Business Handlers ---
  const handleAddInputChange = async (event, newInput) => {
    setAddInput(newInput);
    setAddPlaceId('');
    setAddBusinessName(''); 
    setAddBusinessAddress('');
    if (newInput.trim() === '') {
      setAddSuggestions([]);
      return;
    }
    // Fetch autocomplete suggestions (similar to AddBusinessForm)
    try {
      const response = await fetch(`${API_URL}/autocomplete/?input=${newInput}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        console.log("Autocomplete data:", data); // Log the response
        setAddSuggestions(data.suggestions || []);
        setAddSessionToken(data.session_token);
        console.log("Session token set to:", data.session_token); // Log session token
      } else {
        console.error("Failed to fetch autocomplete suggestions:", response.status);
        setAddSuggestions([]);
      }
    } catch (error) {
      console.error("Error in autocomplete fetch:", error);
      setAddSuggestions([]);
    }
  };

  const handleAddSelect = (newValue) => {
    const selectedSuggestion = addSuggestions.find((s) => s.description === newValue);
    if (selectedSuggestion) {
      setAddPlaceId(selectedSuggestion.place_id);
      setAddBusinessName(selectedSuggestion.name);
      setAddBusinessAddress(selectedSuggestion.address);
    } else {
      setAddPlaceId('');
      setAddBusinessName('');
      setAddBusinessAddress('');
    }
  };

  const handleAddBusinessSubmit = async () => {
    setIsAdding(true);
    setAddError(null);
    if (!addPlaceId || !addBusinessName) {
        setAddError("Please search for and select a valid business.");
        setIsAdding(false);
        return;
    }
    
    try {
      // Match the exact structure of the payload from the original working implementation
      const businessData = {
        name: addBusinessName,
        address: addBusinessAddress,
        place_id: addPlaceId,
        session_token: addSessionToken,
        turnover: addTurnover ? parseInt(addTurnover, 10) : 0,
        currency: addCurrency,
        primary_type: "business",
      };
      
      console.log("Sending business data:", businessData);
      
      const response = await fetch(`${API_URL}/businesses/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(businessData),
      });
      
      if (response.ok) {
        // Clear form fields
        setAddInput(''); setAddPlaceId(''); setAddBusinessName(''); setAddBusinessAddress('');
        setAddTurnover(''); setAddCurrency('USD'); setAddSuggestions([]); setAddError(null);
        
        // Get the newly added business
        const newBusiness = await response.json();
        
        // Refresh data
        await fetchBusinesses();
        
        // Check if we need to auto-select this business
        // We'll select it if it's the first business or if no business is selected
        const hasSelection = selectedBusiness !== null;
        
        // The business needs to be monitored before we can select it
        if (newBusiness && !hasSelection) {
          console.log("Auto-selecting newly added business:", newBusiness.name);
          handleBusinessSelection(newBusiness);
        }
      } else {
        const errorData = await response.json();
        console.error("Error response:", errorData); // Add detailed logging
        
        // Handle different error formats from the API
        if (errorData.detail) {
          // Handle case where detail could be string or object
          setAddError(typeof errorData.detail === 'object' ? errorData.detail : errorData.detail);
        } else {
          setAddError(errorData);
        }
      }
    } catch (error) {
      console.error("Exception during add business:", error); // Add error logging
      setAddError('An error occurred. Please try again later.');
    } finally {
      setIsAdding(false);
    }
  };

  // --- Render Logic ---
  const canAddBusiness = subscriptionStatus.is_active;
  const addDisabled = !canAddBusiness;

  // Calculate total columns for colSpan adjustments
  const numBaseCols = 1 + 1 + 1; // Name, Revenue, Status
  const numOptionalCols = (showAddress ? 1 : 0) + (showRatingStars ? 1 : 0) + (showTotalReviews ? 1 : 0);
  const totalCols = numBaseCols + numOptionalCols;

  return (
    <Box sx={{
      minHeight: '100%',
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column',
      px: { xs: 2, lg: 4 },
      py: { xs: 2, lg: 4 },
      opacity: 0,
      animation: 'fadeIn 1s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      }
    }}>
      <Container maxWidth="xl" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header Section with 3-way split */}
        <Box sx={{
          mb: {
            xs: 2,
            lg: 4
          },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: { xs: 'flex-start', md: 'stretch' },
          width: '100%'
        }}>
          {/* Title Section - 1/3 width - this will determine the height */}
          <Box sx={{
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            order: { xs: 1, md: 1 }
          }}>
            <Box sx={{ mb: { xs: 2, lg: 4 }, display: 'flex', alignItems: 'center' }}>
               <StorefrontIcon sx={{ fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#1976d2', mr: 1 }} />
               <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#0A2540' }}>
                 Businesses
               </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: '#546E7A',
                fontSize: {
                  xs: '0.95rem',
                  lg: '1.1rem'
                }
              }}
            >
              Select a business to view its analytics and review insights
            </Typography>

            {/* Mobile Purchase Button - Only show on extra small screens */}
            {isXsScreen && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 2
              }}>
                {/* Button removed */}
              </Box>
            )}
          </Box>

          {/* Second column - Placeholder for consistency with other pages */}
          <Box sx={{
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            order: { xs: 2, md: 2 },
            display: { xs: 'none', md: 'block' } // Hide on xs screens
          }}>
            {/* This column is empty on the businesses page but keeps layout consistent */}
          </Box>

          {/* Third column - Add Business button for non-mobile users */}
          <Box sx={{
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            display: { xs: 'none', md: 'flex' }, // Hide on xs, show as flex on md+
            justifyContent: 'center',
            alignItems: 'center', // Center vertically
            order: { xs: 3, md: 3 },
            height: '100%' // Take full height from parent
          }}>
            {/* Button removed */}
          </Box>
        </Box>

        {/* Subscription Status Alert */}
         <SubscriptionAlert 
            status={subscriptionStatus.subscription_status} 
            trialEndsAt={subscriptionStatus.trial_ends_at}
            isActive={subscriptionStatus.is_active}
            userRole={subscriptionStatus.user_role}
         />

        {/* Stop Monitoring Confirmation Dialog */}
        <Dialog
          open={stopMonitoringDialogOpen}
          onClose={handleCloseStopMonitoringDialog}
        >
          <DialogTitle>Remove "{businessToStopMonitoring?.name}"?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This action will remove the business from your account. You can add it again later if needed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStopMonitoringDialog}>Cancel</Button>
            <Button onClick={handleConfirmStopMonitoring} color="error" variant="contained">Remove</Button>
          </DialogActions>
        </Dialog>

        {/* Businesses Table */}
        <Box sx={{ mt: 3, alignItems: 'baseline', position: 'relative' }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
            borderRadius: { xs: '12px', sm: '16px', md: '24px' },
            border: '1px solid rgba(25, 118, 210, 0.1)',
            boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
            }
          }}>
            <TableContainer ref={tableContainerRef} sx={{ margin: 0, padding: { xs: 1, md: 2 } }}>
              <Table 
                size="medium" 
                aria-label="businesses table"
                sx={{ 
                  tableLayout: 'fixed',
                  '& th, & td': {
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '100px', sm: '130px', md: '180px' },
                      whiteSpace: 'normal',
                      lineHeight: 1.2,
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}>Name</TableCell>
                    
                    {showAddress && (
                      <TableCell sx={{
                        fontWeight: 700,
                        color: '#0A2540',
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        width: { xs: '120px', sm: '150px', md: '200px' },
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1, sm: 2 },
                      }}>Address</TableCell>
                    )}
                    
                    {showRatingStars && (
                      <TableCell sx={{
                        fontWeight: 700,
                        color: '#0A2540',
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        width: { xs: '80px', sm: '120px' },
                        textAlign: 'center',
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1, sm: 2 },
                      }}>Rating</TableCell>
                    )}
                    
                    {showTotalReviews && (
                      <TableCell sx={{
                        fontWeight: 700,
                        color: '#0A2540',
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        width: { xs: '70px', sm: '100px' },
                        textAlign: 'center',
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1, sm: 2 },
                      }}>Total Reviews</TableCell>
                    )}
                    
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '120px', sm: '140px' },
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}>Revenue</TableCell>
                    
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '60px', sm: '80px' },
                      textAlign: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#1976d2' }} />
                      </TableCell>
                    </TableRow>
                  ) : businessesData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: '#666666' }}>
                          No businesses found. Add a business to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    businessesData
                      .filter(business => {
                        const isPending = business.status === 'pending';
                        const isMonitored = business.is_monitored;
                        return isPending || isMonitored;
                      })
                      .map((business) => {
                        const isSelected = selectedBusiness?.id === business.id;
                        // Determine if interaction is allowed based on subscription status
                        const canInteract = subscriptionStatus.is_active; // Read directly from state

                        return (
                          <TableRow
                            key={business.id}
                            // Use business.is_monitored directly
                            hover={canInteract && business.is_monitored && !isSelected}
                            // Use business.is_monitored directly
                            onClick={canInteract && business.is_monitored ? () => handleBusinessSelection(business) : null}
                            sx={{
                              // Use business.is_monitored directly
                              cursor: canInteract && business.is_monitored ? 'pointer' : 'default',
                              backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                              transition: 'all 0.2s ease-in-out',
                              opacity: canInteract ? 1 : 0.6, 
                              '&:hover': {
                                // Use business.is_monitored directly
                                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : (canInteract && business.is_monitored ? 'rgba(25, 118, 210, 0.04)' : 'inherit'),
                                // Use business.is_monitored directly
                                transform: canInteract && business.is_monitored ? 'translateY(-2px)' : 'none'
                              },
                              '& td': { 
                                borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                                py: { xs: 1.5, sm: 2 },
                                px: { xs: 1, sm: 2 },
                                // Apply greyed-out text if inactive
                                color: !canInteract ? '#9E9E9E' : undefined, // Apply to all cells in the row if inactive
                              }
                            }}
                          >
                            <TableCell sx={{
                              color: '#0A2540', // Base color
                              fontWeight: 500,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'normal',
                              overflow: 'hidden',
                              lineHeight: 1.2,
                              height: 'auto',
                            }}>
                              <Box sx={{
                                width: '100%',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                textOverflow: 'ellipsis',
                              }}>
                                {business.name}
                              </Box>
                            </TableCell>
                            
                            {showAddress && 
                              <TableCell sx={{
                                color: '#546E7A', // Base color
                                fontSize: { xs: '0.875rem', md: '0.95rem' },
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                lineHeight: 1.2,
                                height: 'auto',
                              }}>
                                <Box sx={{
                                  width: '100%',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  textOverflow: 'ellipsis',
                                }}>
                                  {business.address}
                                </Box>
                              </TableCell>
                            }
                            
                            {showRatingStars && (
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Rating
                                    value={business.rating || 0}
                                    precision={0.1}
                                    readOnly
                                    size="small"
                                    sx={{ 
                                      color: '#1976d2',
                                      '& .MuiRating-iconEmpty': {
                                        color: 'rgba(25, 118, 210, 0.3)'
                                      }
                                    }}
                                  />
                                  <Typography sx={{ 
                                    ml: 1, 
                                    color: '#0A2540', // Base color
                                    fontWeight: 600,
                                    fontSize: { xs: '0.875rem', md: '1rem' }
                                  }}>
                                    {business.rating?.toFixed(1) || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            
                            {showTotalReviews && 
                              <TableCell sx={{ 
                                textAlign: 'center',
                                color: '#0A2540', // Base color
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', md: '1rem' }
                              }}>
                                {business.total_reviews || 'N/A'}
                              </TableCell>
                            }
                            
                            <TableCell sx={{
                              color: '#1976d2', // Base color
                              fontWeight: 600,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                            }}>
                               {business.turnover !== null && business.turnover !== undefined 
                                 ? (() => {
                                     const currencyData = currencies[business.currency];
                                     const symbol = currencyData ? currencyData.symbol_native : '';
                                     return `${symbol}${Number(business.turnover).toLocaleString()}`;
                                   })()
                                 : 'N/A'}
                            </TableCell>
                            
                            <TableCell sx={{ textAlign: 'center' }}>
                              {business.status === 'pending' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <LoopIcon sx={{ 
                                    fontSize: '1rem', 
                                    mr: 0.5, 
                                    color: '#F57C00',
                                    animation: 'spin 2s linear infinite',
                                  }} />
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#F57C00' }}>
                                    Processing
                                  </Typography>
                                  <style>{`
                                    @keyframes spin {
                                      0% { transform: rotate(0deg); }
                                      100% { transform: rotate(360deg); }
                                    }
                                  `}</style>
                                </Box>
                              ) : !canInteract ? ( // If subscription is inactive
                                <Tooltip title="Activate subscription to manage business">
                                  <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic' }}>
                                    Inactive
                                  </Typography>
                                </Tooltip>
                              ) : business.is_monitored ? ( // Use business.is_monitored directly
                                <Tooltip title="Monitored - Click to stop monitoring">
                                  {/* Stop monitoring button only active if canInteract */}
                                  <IconButton 
                                    onClick={(e) => handleStopMonitoringClick(e, business)} 
                                    size="small" 
                                    color="primary"
                                    disabled={!canInteract} // Explicitly disable if needed, though parent click handler logic might suffice
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : ( // If subscription is active but not monitored (shouldn't happen with filter, but included for completeness)
                                <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic' }}>
                                  Not Monitored
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
                
                {/* Add Business Form Row */}
                {canAddBusiness && (
                  <TableFooter>
                    {isSmallScreen ? (
                      // --- Mobile/Tablet Layout (Two Rows for Alignment) ---
                      <>
                        <TableRow sx={{ 
                          backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' }
                        }}>
                          <TableCell colSpan={totalCols} sx={{ pt: 2, pb: 1, borderBottom: 'none' }}> 
                            <Autocomplete
                              size="small"
                              options={addSuggestions.map((s) => s.description)}
                              filterOptions={(options) => options}
                              value={addInput}
                              onInputChange={handleAddInputChange}
                              onChange={(event, newValue) => { setAddInput(newValue || ''); handleAddSelect(newValue); }}
                              isOptionEqualToValue={(option, value) => option === value}
                              disabled={isAdding}
                              popupIcon={null}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Search for business" variant="outlined" fullWidth 
                                  size="small" error={addError !== null} />
                              )}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ 
                          backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' }
                        }}>
                          <TableCell colSpan={totalCols - 1} sx={{ pt: 1, pb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                select
                                size="small"
                                placeholder="Currency"
                                value={addCurrency}
                                onChange={(e) => setAddCurrency(e.target.value)}
                                disabled={isAdding}
                                SelectDisplayProps={{ style: { paddingTop: '8.5px', paddingBottom: '8.5px' } }} // Match TextField height
                                sx={{ minWidth: '90px' }} 
                              >
                                {currencyList.map((option) => (
                                  <MenuItem key={option.code} value={option.code}>
                                    {option.code} - {option.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                size="small"
                                placeholder="Revenue"
                                value={addTurnover}
                                onChange={(e) => {
                                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                  if (/^\d*$/.test(numericValue)) {
                                    setAddTurnover(numericValue);
                                  }
                                }}
                                type="text"
                                disabled={isAdding}
                                inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                                sx={{ flex: 1, minWidth: '120px' }} 
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ pt: 1, pb: 2 }}>
                            <Tooltip title={formatErrorMessage(addError) || (isAdding ? "Adding..." : (!addPlaceId ? "Select a business first" : !addTurnover.trim() ? "Enter revenue" : "Add Business"))}>
                              {/* Replace IconButton with styled Button */}
                              <Button 
                                variant="contained" 
                                size="small"
                                color="primary"
                                startIcon={isAdding ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                                onClick={handleAddBusinessSubmit} 
                                disabled={isAdding || !addPlaceId || !addTurnover.trim()}
                                sx={{ 
                                  textTransform: 'none',
                                  fontWeight: 'bold',
                                  borderRadius: '8px',
                                  px: 1.5, 
                                  py: 0.75, // Adjust padding for Button
                                  // Visual indication when disabled
                                  opacity: (isAdding || !addPlaceId || !addTurnover.trim()) ? 0.5 : 1,
                                  cursor: (isAdding || !addPlaceId || !addTurnover.trim()) ? 'not-allowed' : 'pointer',
                                  boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                                  '&:hover': {
                                    boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                                    // Prevent background change on hover if disabled
                                    backgroundColor: theme.palette.primary.dark
                                  },
                                  // Adjust background if needed based on disabled state
                                  backgroundColor: (isAdding || !addPlaceId || !addTurnover.trim()) ? theme.palette.action.disabledBackground : theme.palette.primary.main,
                                }}
                              >
                                Add
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      // --- Desktop Layout (Single Row) ---
                      <TableRow sx={{ 
                        backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' }
                      }}>
                        {/* Name column - Business search field */}
                        <TableCell colSpan={showAddress ? 2 : 1}>
                          <Autocomplete
                            size="small"
                            options={addSuggestions.map((s) => s.description)}
                            filterOptions={(options) => options}
                            value={addInput}
                            onInputChange={handleAddInputChange}
                            onChange={(event, newValue) => { setAddInput(newValue || ''); handleAddSelect(newValue); }}
                            isOptionEqualToValue={(option, value) => option === value}
                            disabled={isAdding}
                            popupIcon={null}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Search for business" variant="outlined" fullWidth 
                                size="small" error={addError !== null} />
                            )}
                            sx={{ minWidth: '100%' }}
                          />
                        </TableCell>
                        
                        {/* Address column - Empty (handled by Name colSpan) */}
                        
                        {/* Currency column (occupies Rating/Total Reviews space) */}
                        {(showRatingStars || showTotalReviews) && (
                          <TableCell 
                            colSpan={(showRatingStars ? 1 : 0) + (showTotalReviews ? 1 : 0)}
                          >
                            <TextField
                              select
                              size="small"
                              label=""
                              placeholder="Currency"
                              value={addCurrency}
                              onChange={(e) => setAddCurrency(e.target.value)}
                              disabled={isAdding}
                              fullWidth // Make it occupy the full cell width
                            >
                              {currencyList.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                  {option.code} - {option.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                        )}
                        
                        {/* Revenue column */}
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Revenue"
                            value={addTurnover}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(/[^0-9]/g, '');
                              if (/^\d*$/.test(numericValue)) {
                                setAddTurnover(numericValue);
                              }
                            }}
                            type="text"
                            disabled={isAdding}
                            inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                            fullWidth // Make it occupy the full cell width
                          />
                        </TableCell>
                        
                        {/* Action column - Add button */}
                        <TableCell align="center">
                          <Tooltip title={formatErrorMessage(addError) || (isAdding ? "Adding..." : (!addPlaceId ? "Select a business first" : !addTurnover.trim() ? "Enter revenue" : "Add Business"))}>
                            {/* Replace IconButton with styled Button */}
                             <Button 
                                variant="contained" 
                                size="small"
                                color="primary"
                                startIcon={isAdding ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                                onClick={handleAddBusinessSubmit} 
                                disabled={isAdding || !addPlaceId || !addTurnover.trim()}
                                sx={{ 
                                  textTransform: 'none',
                                  fontWeight: 'bold',
                                  borderRadius: '8px',
                                  px: 1.5, 
                                  py: 0.75, // Adjust padding for Button
                                  // Visual indication when disabled
                                  opacity: (isAdding || !addPlaceId || !addTurnover.trim()) ? 0.5 : 1,
                                  cursor: (isAdding || !addPlaceId || !addTurnover.trim()) ? 'not-allowed' : 'pointer',
                                  boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                                  '&:hover': {
                                    boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                                    // Prevent background change on hover if disabled
                                    backgroundColor: theme.palette.primary.dark
                                  },
                                  // Adjust background if needed based on disabled state
                                  backgroundColor: (isAdding || !addPlaceId || !addTurnover.trim()) ? theme.palette.action.disabledBackground : theme.palette.primary.main,
                                }}
                              >
                                Add
                              </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
            
            {/* Display limitation message outside the table if user can't add businesses */}
            {(!canAddBusiness) && (
              <Box sx={{ p: { xs: 1, md: 2 }, borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0A2540' }}>Add Business</Typography>
                <Alert severity="info">
                  "Activate your subscription to add businesses."
                </Alert>
              </Box>
            )}
          </Card>
          {/* Display monitoring limit below the card when subscription is active */}
          {subscriptionStatus.is_active && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ mt: 2, color: 'text.secondary' }} 
            >
              Your plan allows you to monitor up to 25 businesses simultaneously.
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Businesses;
